"use client";
import { useEffect } from "react";

type Branding = {
  app_title?: string;
  accent_color?: string;
  logo_data_url?: string;
  contact_email?: string;
};

/**
 * Loads the tenant branding once on mount and propagates the accent color
 * to every CSS variable that drives visible chrome:
 *
 *   --navy      — primary brand color (headlines, nav active, role chips)
 *   --accent    — button background, focus ring
 *   --accent-2  — link hover, secondary fill
 *
 * Without --navy here, the admin's "Accent color" picker only shifted the
 * button colors while the navy headlines stayed the same — the user
 * couldn't actually re-tint the deployment from /admin/branding.
 *
 * Reads from /api/first-run/branding (public, no auth) so the login screen
 * picks up the customer's accent before the user signs in.
 */
export function BrandingProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    fetch("/api/first-run/branding")
      .then((r) => (r.ok ? r.json() : null))
      .then((b: { primary: Branding | null } | null) => {
        const data = b?.primary;
        if (!data) return;
        if (data.app_title) {
          document.title = data.app_title;
        }
        if (data.accent_color) {
          const root = document.documentElement;
          // Drive the whole brand palette from the configured accent.
          // --accent / --accent-2 control buttons + interactive fills.
          root.style.setProperty("--accent", data.accent_color);
          root.style.setProperty("--accent-2", data.accent_color);
          // --navy is the dominant headline color across the app, so
          // shifting it is what makes the deployment actually look
          // re-themed end-to-end.
          root.style.setProperty("--navy", data.accent_color);
        }
      })
      .catch(() => {/* silently ignore — branding is cosmetic */});
  }, []);

  return <>{children}</>;
}
